var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import JoditEditor, { Jodit } from "jodit-react";
import { fontsWithSizes, buttons } from "./utils";
import { useFormContext } from "../Form";
import { classNames } from "../utils/classNames";
var BASIC_THEME_NAME = "piston";
var BASIC_CLASS_NAME = "jodit-wrapper";
export var WYSIWYGEditor = observer(function (_a) {
    var html = _a.html, onChange = _a.onChange, onBlur = _a.onBlur, onInit = _a.onInit, className = _a.className, name = _a.name, _b = _a.customControls, customControls = _b === void 0 ? [] : _b, _c = _a.fonts, fonts = _c === void 0 ? {} : _c, _d = _a.sizes, sizes = _d === void 0 ? {} : _d, _e = _a.width, width = _e === void 0 ? "auto" : _e, _f = _a.height, height = _f === void 0 ? 250 : _f, _g = _a.allowTabNavigation, allowTabNavigation = _g === void 0 ? true : _g, _h = _a.disabled, disabled = _h === void 0 ? false : _h, _j = _a.useNativeTooltip, useNativeTooltip = _j === void 0 ? false : _j, _k = _a.toolbarAdaptive, toolbarAdaptive = _k === void 0 ? false : _k, _l = _a.showPlaceholder, showPlaceholder = _l === void 0 ? true : _l;
    var values = useFormContext().values;
    // Freeze the initial value so jodit-react's useEffect([value]) never fires
    // after mount — we manage external updates imperatively via joditRef.
    var initialValueRef = useRef(html);
    // Tracks the latest value the user typed (or the last external value we
    // pushed), so we can restore it after a config-triggered Jodit reinit.
    var lastKnownValueRef = useRef(html);
    // Direct reference to the live Jodit instance, set via afterInit.
    var joditRef = useRef(null);
    // Track each control's disabled state in a ref so isDisabled() always
    // returns the latest value without forcing a config recreation (and
    // therefore a Jodit reinit + cursor reset) on every disabled change.
    var controlDisabledRef = useRef({});
    // Only changes when controls are added or removed — not when disabled changes.
    var controlNamesKey = customControls.map(function (c) { var _a; return (_a = c === null || c === void 0 ? void 0 : c.name) !== null && _a !== void 0 ? _a : ""; }).join(",");
    var disabledKey = customControls
        .map(function (c) { return "".concat(c === null || c === void 0 ? void 0 : c.name, ":").concat(c === null || c === void 0 ? void 0 : c.disabled); })
        .join(",");
    useEffect(function () {
        customControls.forEach(function (control) {
            if (control) {
                controlDisabledRef.current[control.name] = control.disabled;
            }
        });
    }, [disabledKey]);
    var handleEvent = useCallback(function (handler, newValue) {
        if (typeof handler !== "function")
            return;
        // Record before calling handler so lastKnownValueRef is always current
        // when the parent re-renders and html prop propagates back down.
        lastKnownValueRef.current = newValue;
        handler(newValue);
        if (name) {
            values.set(name, newValue);
        }
    }, [name, values]);
    // Push external html changes into Jodit without going through the
    // jodit-react value prop, which resets cursor position. Skip if the
    // user is currently focused — their live content takes precedence.
    useEffect(function () {
        var _a, _b;
        var editor = joditRef.current;
        if (!editor || html === lastKnownValueRef.current) {
            lastKnownValueRef.current = html;
            return;
        }
        if ((_b = (_a = editor.s) === null || _a === void 0 ? void 0 : _a.isFocused) === null || _b === void 0 ? void 0 : _b.call(_a)) {
            // Keep lastKnownValueRef pointing at the live editor value while focused.
            lastKnownValueRef.current = editor.value;
            return;
        }
        lastKnownValueRef.current = html;
        editor.value = html;
    }, [html]);
    useEffect(function () {
        var _a, _b, _c;
        (_c = (_b = (_a = joditRef.current) === null || _a === void 0 ? void 0 : _a.toolbar) === null || _b === void 0 ? void 0 : _b.update) === null || _c === void 0 ? void 0 : _c.call(_b);
    }, [disabledKey]);
    var menuControls = useMemo(function () {
        return customControls.reduce(function (o, control) {
            var _a;
            return (__assign(__assign({}, o), (_a = {}, _a[control.name] = {
                name: control.label,
                tooltip: control.tooltip,
                exec: control.onClick,
                isDisabled: function () { var _a; return (_a = controlDisabledRef.current[control.name]) !== null && _a !== void 0 ? _a : false; },
            }, _a)));
        }, {});
    }, [controlNamesKey]);
    var config = useMemo(function () { return (__assign(__assign({}, Jodit.defaultOptions), { controls: __assign(__assign({}, fontsWithSizes(fonts, sizes)), menuControls), events: {
            afterInit: function (instance) {
                joditRef.current = instance;
                // Restore content after a config-triggered reinit; jodit-react
                // reinitialises from the frozen initialValueRef, so we need to
                // write back whatever the user had typed since mount.
                if (instance.value !== lastKnownValueRef.current) {
                    instance.value = lastKnownValueRef.current;
                }
                if (typeof onInit === "function") {
                    onInit(instance);
                }
            },
        }, extraButtons: Object.keys(menuControls), theme: BASIC_THEME_NAME, disablePlugins: "powered-by-jodit", buttons: buttons, disabled: disabled, width: width, height: height, allowTabNavigation: allowTabNavigation, toolbarAdaptive: toolbarAdaptive, useNativeTooltip: useNativeTooltip, showPlaceholder: showPlaceholder })); }, [
        menuControls,
        disabled,
        width,
        height,
        allowTabNavigation,
        toolbarAdaptive,
        useNativeTooltip,
    ]);
    var handleChange = useCallback(function (newValue) { return handleEvent(onChange, newValue); }, [handleEvent, onChange]);
    var handleBlur = useCallback(function (newValue) { return handleEvent(onBlur, newValue); }, [handleEvent, onBlur]);
    return (React.createElement(JoditEditor, { value: initialValueRef.current, config: config, className: classNames(BASIC_CLASS_NAME, className), onChange: handleChange, onBlur: handleBlur }));
});
